<template>
  <wiskModal :visible="!!editAction" @hide="setGlobalAction({ type: 'recipeImportUpload', action: null })" :title="translations.txtPOSItemsImportRecipesTitle"
    size="lg" :okDisabled="!file || loading" :hideOK="!!results" @ok="save" :okText="translations.txtGenericUpload" :cancelText="results ? translations.txtGenericClose : translations.txtGenericClose" preventOKClose>
    <div v-if="editAction" class="wisk-recipe-import-upload" style="min-height: 450px;">
      <b-container fluid class="p-4">

        <template v-if="!results">
          <b-row>
            <b-col class="mb-4">
              <p>{{ translations.txtPOSItemsImportRecipesMessage }}</p>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="text-center mb-4">
              <b-button variant="outline-success" size="md" v-tooltip="translations.txtPOSItemsImportRecipesDownloadTemplate" @click="downloadTemplate">
                <icon name="wisk-excel" :scale="0.7" style="margin-top: -2;" />
                <span> {{ translations.txtPOSItemsImportRecipesDownloadTemplate }} </span>
              </b-button>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-form-group label="" class="">
                <b-input-group>
                  <fileInput accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @reset="file = null" v-model="file" />
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </template>

        <template v-if="results">
          <b-row class="mb-3 align-items-center">
            <b-col>
              <h5 class="mb-0">{{ translations.txtPOSItemsImportRecipesResults }}</h5>
            </b-col>
            <b-col cols="auto">
              <b-button variant="outline-secondary" size="sm" @click="copyToClipboard">
                {{ copied ? translations.txtPOSItemsImportRecipesCopied : translations.txtPOSItemsImportRecipesCopyReport }}
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="results.subrecipes && results.subrecipes.length" class="mb-2">
            <b-col>
              <strong>
                {{ translations.txtPOSItemsImportRecipesBatches }}
                ({{ subrecipesCreated }} {{ translations.txtGenericCreated }}, {{ subrecipesUpdated }} {{ translations.txtGenericUpdated }})
              </strong>
              <div style="max-height: 280px; overflow-y: auto;">
                <ul class="mt-1 mb-0">
                  <li v-for="r in results.subrecipes.slice(0, MAX_DISPLAY_ITEMS)" :key="r.id" :class="r.action === 'created' ? 'text-success' : 'text-primary'">
                    {{ r.title }}
                    <span class="text-muted small">({{ r.ingredient_count }} {{ r.ingredient_count !== 1 ? translations.txtPOSItemsImportRecipesIngredients : translations.txtPOSItemsImportRecipesIngredient }})</span>
                    — {{ r.action === 'created' ? translations.txtGenericCreated : translations.txtGenericUpdated }}
                  </li>
                </ul>
                <p v-if="results.subrecipes.length > MAX_DISPLAY_ITEMS" class="text-muted small mt-1 mb-0">
                  {{ translations.translate('txtPOSItemsImportRecipesAndMore', { '{count}': results.subrecipes.length - MAX_DISPLAY_ITEMS }) }}
                </p>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="results.pos_items && results.pos_items.length" class="mb-2">
            <b-col>
              <strong>
                {{ translations.txtPOSItemsTitle }}
                ({{ posItemsCreated }} {{ translations.txtGenericCreated }}, {{ posItemsUpdated }} {{ translations.txtGenericUpdated }})
              </strong>
              <div style="max-height: 280px; overflow-y: auto;">
                <ul class="mt-1 mb-0">
                  <li v-for="r in results.pos_items.slice(0, MAX_DISPLAY_ITEMS)" :key="r.id" :class="r.action === 'created' ? 'text-success' : 'text-primary'">
                    {{ r.title }}
                    <span class="text-muted small">({{ r.ingredient_count }} {{ r.ingredient_count !== 1 ? translations.txtPOSItemsImportRecipesIngredients : translations.txtPOSItemsImportRecipesIngredient }})</span>
                    — {{ r.action === 'created' ? translations.txtGenericCreated : translations.txtGenericUpdated }}
                  </li>
                </ul>
                <p v-if="results.pos_items.length > MAX_DISPLAY_ITEMS" class="text-muted small mt-1 mb-0">
                  {{ translations.translate('txtPOSItemsImportRecipesAndMore', { '{count}': results.pos_items.length - MAX_DISPLAY_ITEMS }) }}
                </p>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="results.skipped_ingredients && results.skipped_ingredients.length" class="mb-2">
            <b-col>
              <strong class="text-warning">{{ translations.txtPOSItemsImportRecipesSkipped }} ({{ results.skipped_ingredients.length }})</strong>
              <div style="max-height: 280px; overflow-y: auto;">
                <ul class="mt-1 mb-0">
                  <li v-for="(s, i) in results.skipped_ingredients.slice(0, MAX_DISPLAY_ITEMS)" :key="i" class="text-warning">
                    {{ s.recipe_name }} › {{ s.ingredient_name }} (External ID: {{ s.external_id }})
                    — <em>{{ skippedReasonLabel(s.reason) }}</em>
                  </li>
                </ul>
                <p v-if="results.skipped_ingredients.length > MAX_DISPLAY_ITEMS" class="text-muted small mt-1 mb-0">
                  {{ translations.translate('txtPOSItemsImportRecipesAndMore', { '{count}': results.skipped_ingredients.length - MAX_DISPLAY_ITEMS }) }}
                </p>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="!results.subrecipes?.length && !results.pos_items?.length" class="mb-2">
            <b-col>
              <p class="text-muted">{{ translations.txtPOSItemsImportRecipesEmpty }}</p>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col class="text-center">
              <b-button variant="primary" @click="reset">{{ translations.txtPOSItemsImportRecipesImportAnother }}</b-button>
            </b-col>
          </b-row>
        </template>

      </b-container>

      <wiskLoading :loading="loading" />
    </div>
  </wiskModal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api from '@/api'
import fileInput from '@/components/common/FileInput'

const MAX_DISPLAY_ITEMS = 200

const SKIPPED_REASON_KEYS = {
  external_id_not_found: 'txtPOSItemsImportRecipesReasonExternalIdNotFound',
  item_id_not_found: 'txtPOSItemsImportRecipesReasonItemIdNotFound',
  measurement_type_mismatch: 'txtPOSItemsImportRecipesReasonMeasurementMismatch',
  circular_reference: 'txtPOSItemsImportRecipesReasonCircularReference',
}

export default {
  name: 'RecipeImportUploader',
  components: { fileInput },
  props: {
    editAction: {
      type: Object
    }
  },
  data() {
    return {
      loading: false,
      file: null,
      results: null,
      copied: false,
      MAX_DISPLAY_ITEMS,
    }
  },
  computed: {
    ...mapState(['translations']),
    subrecipesCreated() {
      return this.results?.subrecipes?.filter(r => r.action === 'created').length || 0
    },
    subrecipesUpdated() {
      return this.results?.subrecipes?.filter(r => r.action === 'updated').length || 0
    },
    posItemsCreated() {
      return this.results?.pos_items?.filter(r => r.action === 'created').length || 0
    },
    posItemsUpdated() {
      return this.results?.pos_items?.filter(r => r.action === 'updated').length || 0
    },
  },
  methods: {
    ...mapActions(['setGlobalAction', 'notify']),
    skippedReasonLabel(reason) {
      const key = SKIPPED_REASON_KEYS[reason]
      return (key && this.translations[key]) || reason
    },
    buildReportText() {
      const r = this.results
      const t = this.translations
      const lines = [t.txtPOSItemsImportRecipesReportTitle, '']

      if (r.subrecipes?.length) {
        lines.push(t.translate('txtPOSItemsImportRecipesReportBatchesHeader', { '{created}': this.subrecipesCreated, '{updated}': this.subrecipesUpdated }))
        for (const item of r.subrecipes) {
          const action = item.action === 'created' ? t.txtGenericCreated : t.txtGenericUpdated
          const ingredientWord = item.ingredient_count !== 1 ? t.txtPOSItemsImportRecipesIngredients : t.txtPOSItemsImportRecipesIngredient
          lines.push(`  [${action}] ${item.title} (${item.ingredient_count} ${ingredientWord})`)
        }
        lines.push('')
      }

      if (r.pos_items?.length) {
        lines.push(t.translate('txtPOSItemsImportRecipesReportPosItemsHeader', { '{created}': this.posItemsCreated, '{updated}': this.posItemsUpdated }))
        for (const item of r.pos_items) {
          const action = item.action === 'created' ? t.txtGenericCreated : t.txtGenericUpdated
          const ingredientWord = item.ingredient_count !== 1 ? t.txtPOSItemsImportRecipesIngredients : t.txtPOSItemsImportRecipesIngredient
          lines.push(`  [${action}] ${item.title} (${item.ingredient_count} ${ingredientWord})`)
        }
        lines.push('')
      }

      if (r.skipped_ingredients?.length) {
        lines.push(t.translate('txtPOSItemsImportRecipesReportSkippedHeader', { '{count}': r.skipped_ingredients.length }))
        for (const s of r.skipped_ingredients) {
          lines.push(`  ${s.recipe_name} › ${s.ingredient_name} (External ID: ${s.external_id}) — ${this.skippedReasonLabel(s.reason)}`)
        }
      }

      return lines.join('\n')
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.buildReportText()).then(() => {
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      })
    },
    downloadTemplate() {
      this.loading = true
      api.downloadBlob('recipes/export', 'Venue_recipes.xlsx')
        .catch(() => {
          this.notify({ title: this.translations.txtGenericError, message: this.translations.txtPOSItemsImportRecipesDownloadError, type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    save() {
      const formData = new FormData()
      formData.append('data', this.file)
      this.loading = true

      api
        .uploadRecipeImportXls(formData)
        .then(response => {
          this.results = response
          this.file = null
        })
        .catch(() => {
          this.notify({ title: this.translations.txtGenericError, message: this.translations.txtPOSItemsImportRecipesUploadError, type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    reset() {
      this.results = null
      this.file = null
    }
  },
  watch: {
    editAction(val) {
      if (!val) {
        this.results = null
        this.file = null
      }
    }
  }
}
</script>
